import React, { useState } from 'react';
import { Button } from '../ui/Button';

interface TradingAPIInformationProps {
  apiUrl: string;
  tradeEndpoint: string;
}

export const TradingAPIInformation: React.FC<TradingAPIInformationProps> = ({ apiUrl, tradeEndpoint }) => {
  const [showApiDetails, setShowApiDetails] = useState(false);
  const exampleWebhookPayload = {
    user: 'your_user_id',
    position: 'long',
    prod: 'true',
    apiKey: 'your_api_key',
    symbol: 'BTCUSDT.P',
    amount: '1',
    leverage: '20.0',
    takeProfitRatio: '1.008',
    stopLossRatio: '0.991',
    useProxy: 'true',
    usePostOnlyOrders: 'true',
    useTrailingStop: 'true',
    trailingStopActivationRatio: '1.005',
    trailingStopRatio: '0.990',
    tpOrderType: 'mark',
    slOrderType: 'mark',
    tpslPriceThreshold: '0.0001',
    autoCancelMinutes: '30'
  };

  const exampleCloseLongPayload = {
    user: 'your_user_id',
    position: 'close_long',
    prod: 'true',
    apiKey: 'your_api_key',
    symbol: 'BTCUSDT.P',
    amount: '1',
    useProxy: 'true'
  };

  const exampleCloseShortPayload = {
    user: 'your_user_id',
    position: 'close_short',
    prod: 'true',
    apiKey: 'your_api_key',
    symbol: 'BTCUSDT.P',
    amount: '1',
    useProxy: 'true'
  };

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <h2 className="text-sm font-semibold text-gray-700 mb-2">Trading API Information</h2>
          <div className="space-y-1 text-sm">
            <p className="text-gray-600">
              <span className="font-medium">API Base URL:</span>{' '}
              <code className="bg-gray-100 px-2 py-0.5 rounded text-xs">{apiUrl}</code>
            </p>
            <p className="text-gray-600">
              <span className="font-medium">Trade Endpoint:</span>{' '}
              <code className="bg-gray-100 px-2 py-0.5 rounded text-xs">POST {tradeEndpoint}</code>
            </p>
            <p className="text-gray-600">
              <span className="font-medium">Response:</span>{' '}
              202 Accepted with correlation ID &amp; execution ARN for Step Functions tracking
            </p>
          </div>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setShowApiDetails(!showApiDetails)}
        >
          {showApiDetails ? 'Hide Details' : 'Show Details'}
        </Button>
      </div>

      {showApiDetails && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Query Parameters:</h3>
          <div className="bg-white rounded p-3 text-xs font-mono space-y-1">
            <div className="font-bold"><span className="text-blue-600">user</span>: string - User identifier (required)</div>
            <div className="font-bold"><span className="text-blue-600">position</span>: &quot;long&quot; | &quot;short&quot; | &quot;close_long&quot; | &quot;close_short&quot; - Trading position or closure (required)</div>
            <div><span className="text-blue-600">prod</span>: &quot;true&quot; | &quot;false&quot; - Use production (true) or testnet (false)</div>
            <div className="font-bold"><span className="text-blue-600">apiKey</span>: string - API key for authentication (required)</div>
            <div className="font-bold"><span className="text-blue-600">amount</span>: string - Amount of BTC to trade (required)</div>
            <div className="font-bold"><span className="text-blue-600">symbol</span>: string - Symbol to place order for (e.g. BTCUSDT.P) (required)</div>
            <div className="font-bold"><span className="text-blue-600">leverage</span>: string - Leverage amount (required)</div>
            <div className="font-bold"><span className="text-blue-600">stopLossRatio</span>: string - Stop loss percentage (required)</div>
            <div className="font-bold"><span className="text-blue-600">takeProfitRatio</span>: string - Take profit percentage (required)</div>
            <div><span className="text-blue-600">useProxy</span>: &quot;true&quot; | &quot;false&quot; - Use proxy connection (optional)</div>
            <div><span className="text-blue-600">usePostOnlyOrders</span>: &quot;true&quot; | &quot;false&quot; - Use POST-only orders with market fallback (optional)</div>
            <div><span className="text-blue-600">marketPriceOverride</span>: string - Override market price for testing (testnet only, optional)</div>
            <div><span className="text-blue-600">useTrailingStop</span>: &quot;true&quot; | &quot;false&quot; - Enable trailing stop functionality (optional)</div>
            <div><span className="text-blue-600">trailingStopActivationRatio</span>: string - Ratio for trailing stop activation price (e.g., &quot;1.005&quot; for 0.5% above entry) (optional)</div>
            <div><span className="text-blue-600">trailingStopRatio</span>: string - Ratio for trailing stop distance from activation price (e.g., &quot;0.990&quot;) (optional)</div>
            <div><span className="text-blue-600">tpOrderType</span>: &quot;market&quot; | &quot;limit&quot; | &quot;mark&quot; - Take profit order type (defaults to limit when omitted)</div>
            <div><span className="text-blue-600">slOrderType</span>: &quot;market&quot; | &quot;limit&quot; | &quot;mark&quot; - Stop loss order type (defaults to mark-price triggered market exit)</div>
            <div><span className="text-blue-600">tpslPriceThreshold</span>: string - Price threshold for TP/SL limit orders (e.g., &quot;0.0001&quot; for 0.01%) (default: 0.01%) (optional, only used when a corresponding order type is limit or mark)</div>
            <div><span className="text-blue-600">autoCancelMinutes</span>: string - Minutes before unfilled entry orders auto-cancel (production only, defaults to 30, set &quot;0&quot; to disable)</div>
          </div>

          <h3 className="text-sm font-semibold text-gray-700 mt-3 mb-2">Example Requests:</h3>
          <div className="space-y-2">
            <div className="bg-white rounded p-3 text-xs">
              <p className="text-gray-600 mb-1">Long position on testnet:</p>
              <code className="block bg-gray-100 p-2 rounded overflow-x-auto">
                POST {tradeEndpoint}?user=john_doe&position=long&prod=false&apiKey=abc123&amount=0.001&symbol=BTCUSDT.P&leverage=10&stopLossRatio=2&takeProfitRatio=4
              </code>
            </div>
            <div className="bg-white rounded p-3 text-xs">
              <p className="text-gray-600 mb-1">Short position on production with POST-only orders:</p>
              <code className="block bg-gray-100 p-2 rounded overflow-x-auto">
                POST {tradeEndpoint}?user=jane_smith&position=short&prod=true&apiKey=xyz789&amount=0.01&symbol=BTCUSDT.P&leverage=5&stopLossRatio=1.5&takeProfitRatio=3&useProxy=true&usePostOnlyOrders=true
              </code>
            </div>
            <div className="bg-white rounded p-3 text-xs">
              <p className="text-gray-600 mb-1">Long position with trailing stop and explicit TP/SL order types (TradingView webhook format):</p>
              <code className="block bg-gray-100 p-2 rounded overflow-x-auto">
                {JSON.stringify(exampleWebhookPayload)}
              </code>
            </div>
            <div className="bg-white rounded p-3 text-xs">
              <p className="text-gray-600 mb-1">Close long position (closes entire position):</p>
              <code className="block bg-gray-100 p-2 rounded overflow-x-auto">
                {JSON.stringify(exampleCloseLongPayload)}
              </code>
            </div>
            <div className="bg-white rounded p-3 text-xs">
              <p className="text-gray-600 mb-1">Close short position (closes entire position):</p>
              <code className="block bg-gray-100 p-2 rounded overflow-x-auto">
                {JSON.stringify(exampleCloseShortPayload)}
              </code>
            </div>
          </div>

          <h3 className="text-sm font-semibold text-gray-700 mt-3 mb-2">Response Codes:</h3>
          <div className="bg-white rounded p-3 text-xs space-y-1">
            <div><span className="text-green-600">202</span>: Workflow accepted &mdash; correlation ID + execution ARN returned</div>
            <div><span className="text-green-600">200</span>: Successful trade execution (position closures and idempotent reversals)</div>
            <div><span className="text-red-600">400</span>: Bad request or trade execution failed</div>
            <div><span className="text-red-600">403</span>: API key mismatch</div>
            <div><span className="text-red-600">404</span>: User not found</div>
            <div><span className="text-red-600">409</span>: Conflict - duplicate position rejected (same direction as existing)</div>
            <div><span className="text-red-600">500</span>: Server error</div>
          </div>

          <h3 className="text-sm font-semibold text-gray-700 mt-3 mb-2">Recent Improvements:</h3>
          <div className="bg-white rounded p-3 text-xs space-y-2">
            <div><span className="text-blue-600">✓</span> <strong>Step Functions Workflow:</strong> Trade lifecycle runs through AWS Step Functions with end-to-end history and retries</div>
            <div><span className="text-blue-600">✓</span> <strong>Position Closure:</strong> New close_long and close_short positions for closing active trades</div>
            <div><span className="text-blue-600">✓</span> <strong>TP/SL Order Types:</strong> Configure take profit and stop loss independently as limit, mark-based limit, or market with configurable thresholds</div>
            <div><span className="text-blue-600">✓</span> <strong>Dynamic Tick Size:</strong> API now fetches symbol-specific tick sizes instead of hardcoded values</div>
            <div><span className="text-blue-600">✓</span> <strong>POST-only Orders:</strong> New strategy attempts POST-only orders first, falls back to market orders if needed</div>
            <div><span className="text-blue-600">✓</span> <strong>Enhanced Validation:</strong> Improved order book validation ensures reliable price data</div>
            <div><span className="text-blue-600">✓</span> <strong>Symbol Agnostic:</strong> Supports any trading pair, not just BTCUSDT</div>
            <div><span className="text-blue-600">✓</span> <strong>Trailing Stop Support:</strong> Added trailing stop functionality with activation price and distance parameters</div>
            <div><span className="text-blue-600">✓</span> <strong>Position Reversal:</strong> Automatically reverses positions when opposite signal received (doubles order size)</div>
            <div><span className="text-blue-600">✓</span> <strong>Auto-Cancel Watchdog:</strong> Step Functions loop cancels unfilled entry orders after the configured timeout (default 30 minutes)</div>
          </div>

          <h3 className="text-sm font-semibold text-gray-700 mt-3 mb-2">TP/SL Order Types Explained:</h3>
          <div className="bg-white rounded p-3 text-xs space-y-2">
            <div><strong>Default Behavior:</strong></div>
            <div className="ml-4">
              • <code>tpOrderType</code> defaults to <strong>limit</strong> when omitted (unchanged)
            </div>
            <div className="ml-4">
              • <code>slOrderType</code> now defaults to <strong>mark</strong>, using Bybit&apos;s mark price for trigger evaluation while executing as a market exit for faster fills
            </div>
            <div className="ml-4">
              • Limit orders aim to improve fills during volatile conditions by anchoring price slightly past the trigger
            </div>
            <div className="mt-2"><strong>Customising Each Leg:</strong></div>
            <div className="ml-4">
              • Choose <code>{'tpOrderType="market"'}</code> to take profit immediately while keeping the stop loss as a limit order (or vice versa)
            </div>
            <div className="ml-4">
              • Select <code>{'tpOrderType="mark"'}</code> or <code>{'slOrderType="mark"'}</code> to base triggers on Bybit&apos;s mark price. TP keeps the configured limit buffer; SL now converts to a market order at trigger
            </div>
            <div className="ml-4">
              • Mixing order types lets you combine aggressive exits with protected stops depending on strategy needs
            </div>
            <div className="mt-2"><strong>Price Threshold:</strong></div>
            <div className="ml-4">
              • The <code>tpslPriceThreshold</code> (default: 0.01%) only applies when the associated order type is <strong>limit</strong> or <strong>mark</strong>
            </div>
            <div className="ml-4">
              • Long positions: limit TP/SL are nudged below the trigger; short positions nudge above to increase fill probability
            </div>
            <div className="mt-2"><strong>Backwards Compatibility:</strong></div>
            <div className="ml-4">
              • Set <code>{'tpOrderType="market"'}</code> for immediate TP execution
            </div>
            <div className="ml-4">
              • Use <code>{'slOrderType="limit"'}</code> to keep stop losses resting as limit orders instead of market exits
            </div>
            <div className="ml-4">
              • Existing webhooks that omit these fields continue to send limit TP orders and mark-triggered market SL orders automatically
            </div>
          </div>

          <h3 className="text-sm font-semibold text-gray-700 mt-3 mb-2">Trailing Stop Explained:</h3>
          <div className="bg-white rounded p-3 text-xs space-y-2">
            <div><strong>⚡ Key Features:</strong></div>
            <div className="ml-4">
              • <strong>Dynamic Tick Size:</strong> Automatically fetches symbol-specific tick size for precise price rounding
            </div>
            <div className="ml-4">
              • <strong>Distance Calculation:</strong> Trailing stop uses absolute USDT distance, not price levels
            </div>
            <div className="ml-4">
              • <strong>Ratio Validation:</strong> Stop ratio must be between 0 and 1 (e.g., 0.990 for 1% trailing)
            </div>

            <div className="mt-2"><strong>For Long Positions:</strong></div>
            <div className="ml-4">
              • <strong>Activation Price:</strong> Set above entry price (e.g., ratio 1.005 = 0.5% above entry)
            </div>
            <div className="ml-4">
              • <strong>Trailing Distance:</strong> Absolute distance in USDT that stop loss trails below highest price
            </div>
            <div className="ml-4">
              • <strong>Example:</strong> Entry $100, activation at $100.50 (1.005), trails $1.005 below highest (0.990 = 1% distance)
            </div>

            <div className="mt-2"><strong>For Short Positions:</strong></div>
            <div className="ml-4">
              • <strong>Activation Price:</strong> Set below entry price (automatically inverted - use same ratio format as long)
            </div>
            <div className="ml-4">
              • <strong>Trailing Distance:</strong> Absolute distance in USDT that stop loss trails above lowest price
            </div>
            <div className="ml-4">
              • <strong>Example:</strong> Entry $100, ratio 1.005 converts to activation at $99.50, trails $0.995 above lowest (0.990 = 1% distance)
            </div>

            <div className="mt-2"><strong>📝 Technical Details:</strong></div>
            <div className="ml-4">
              • <strong>Distance Formula:</strong> trailingStop = activePrice × (1 - stopRatio)
            </div>
            <div className="ml-4">
              • <strong>Precision:</strong> All prices rounded to each symbol tick size (e.g., 0.5 for BTCUSDT, 0.01 for ETHUSDT)
            </div>
            <div className="ml-4">
              • <strong>API Parameters:</strong> activePrice (activation price) and trailingStop (distance in USDT)
            </div>
          </div>

          <h3 className="text-sm font-semibold text-gray-700 mt-3 mb-2">Position Closure:</h3>
          <div className="bg-white rounded p-3 text-xs space-y-2">
            <div><strong>🔐 Close Positions:</strong></div>
            <div className="ml-4">
              • <strong>close_long:</strong> Closes an active long position completely
            </div>
            <div className="ml-4">
              • <strong>close_short:</strong> Closes an active short position completely
            </div>
            <div className="mt-2"><strong>🎯 Closure Behavior:</strong></div>
            <div className="ml-4">
              • <strong>Position Verification:</strong> Checks that the correct position type exists before closure
            </div>
            <div className="ml-4">
              • <strong>Full Closure:</strong> Always closes the entire position (amount parameter is ignored)
            </div>
            <div className="ml-4">
              • <strong>Idempotent:</strong> Returns success if no position exists (safe to retry)
            </div>
            <div className="ml-4">
              • <strong>P&L Included:</strong> Response includes realized and unrealized P&L information
            </div>
            <div className="mt-2"><strong>📊 Response Details:</strong></div>
            <div className="ml-4">
              • Position details (entry price, size, P&L)
            </div>
            <div className="ml-4">
              • Closure order information (order ID, execution price)
            </div>
            <div className="ml-4">
              • Email notifications sent for all closure attempts
            </div>
          </div>

          <h3 className="text-sm font-semibold text-gray-700 mt-3 mb-2">Position Management & Safety:</h3>
          <div className="bg-white rounded p-3 text-xs space-y-2">
            <div><strong>🔒 Safety Check Behavior:</strong></div>
            <div className="ml-4">
              • <strong>Same Direction Signal:</strong> Rejected to prevent duplicate positions (e.g., long signal when already long)
            </div>
            <div className="ml-4">
              • <strong>Opposite Direction Signal:</strong> Executes position reversal automatically
            </div>
            <div className="mt-2"><strong>🔄 Position Reversal:</strong></div>
            <div className="ml-4">
              • Automatically doubles the order size to close existing position and open new one
            </div>
            <div className="ml-4">
              • Example: 1 BTC long position + 1 BTC short signal = 2 BTC short order (closes 1 BTC long, opens 1 BTC short)
            </div>
            <div className="ml-4">
              • Uses POST-only orders if specified in webhook signal
            </div>
            <div className="ml-4">
              • Email notifications clearly indicate when a reversal occurs
            </div>
            <div className="ml-4">
              • <strong>Auto-Cancel Safety:</strong> Entry orders are re-checked every few minutes and automatically cancelled after <code>autoCancelMinutes</code> (default 30) if they remain unfilled
            </div>
            <div className="ml-4">
              • Set <code>autoCancelMinutes</code> to <strong>0</strong> to disable the safeguard or provide a custom timeout per signal
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
